import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Loader2, ShoppingCart, Download, Check } from "lucide-react";

const CATEGORY_ORDER = [
  "Full Frequency Analyses",
  "Navagraha · 9-Planet Packages",
  "27-Nakshatra Packages",
  "Value Bundles",
];

const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function AstroRemedies() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [owned, setOwned] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/content/remedies").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => {
    if (user && typeof user === "object") {
      api.get("/remedies/my").then((r) => setOwned(r.data.product_ids || [])).catch(() => {});
    }
  }, [user]);

  const buy = async (p) => {
    if (!user || typeof user !== "object") { navigate("/login"); return; }
    setBusy(p.id);
    try {
      const { data } = await api.post("/remedies/checkout", { product_id: p.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not start checkout.");
      setBusy(null);
    }
  };

  const access = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.get(`/remedies/access/${p.id}`);
      if (data.configuration_required) {
        toast.info("Your content link will be added shortly.");
        return;
      }
      if (!data.download_url) {
        toast.info("Your content link will be added shortly.");
        return;
      }
      const resp = await api.get(data.download_url, { responseType: "blob" });
      const cd = resp.headers?.["content-disposition"] || "";
      const nameMatch = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      const filename = nameMatch ? decodeURIComponent(nameMatch[1]) : (data.title || "remedy");
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: resp.data.type || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const grouped = {};
  (items || []).forEach((it) => { (grouped[it.category || "Other"] ||= []).push(it); });
  const categories = [
    ...CATEGORY_ORDER.filter((c) => grouped[c]),
    ...Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  return (
    <div className="min-h-screen bg-[#070A0F]">
      <div className="relative overflow-hidden border-b border-white/[0.06]">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-[36rem] rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-16 text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-cyan-300 mb-4"><Sparkles size={14} strokeWidth={1.5} /> Nakshatra Mitra · Remedies</span>
          <h1 className="text-4xl sm:text-5xl tracking-tight text-slate-50 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Astro Remedies Store</h1>
          <p className="text-slate-400 max-w-xl mx-auto">Frequency sounds, mantras and healing collections tuned to your planets and nakshatras. Instant digital delivery after purchase.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        {items === null ? (
          <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-cyan-300" strokeWidth={1.5} /></div>
        ) : items.length === 0 ? (
          <p className="text-center text-slate-400 py-24">No remedies published yet.</p>
        ) : (
          <div className="space-y-14">
            {categories.map((cat) => (
              <section key={cat} data-testid={`remedy-category-${cat}`}>
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="text-2xl text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>{cat}</h2>
                  <span className="h-px flex-1 bg-white/[0.06]" />
                  <span className="text-xs text-slate-500">{grouped[cat].length} items</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {grouped[cat].map((p, i) => {
                    const isOwned = owned.includes(p.id);
                    const discount = p.regular_price && p.offer_price && p.regular_price > p.offer_price
                      ? Math.round((1 - p.offer_price / p.regular_price) * 100) : 0;
                    return (
                      <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.05 }}
                        className="flex flex-col rounded-2xl border border-white/[0.06] bg-[#0C1017] p-6 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-cyan-400/25" data-testid={`remedy-card-${p.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="text-base text-slate-100 leading-snug" style={{ fontFamily: "Outfit, sans-serif" }}>{p.title}</h3>
                          {discount > 0 && <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-cyan-400/15 text-cyan-300 border border-cyan-400/25">{discount}% off</span>}
                        </div>
                        <p className="text-xs text-slate-400 leading-relaxed flex-1 mb-4 line-clamp-4">{p.description}</p>
                        <div className="flex items-end gap-2 mb-4">
                          <span className="text-2xl font-bold text-amber-300" style={{ fontFamily: "Outfit, sans-serif" }}>{inr(p.offer_price)}</span>
                          {discount > 0 && <span className="text-sm text-slate-500 line-through mb-0.5">{inr(p.regular_price)}</span>}
                        </div>
                        {discount > 0 && (
                          <p className="text-[11px] text-emerald-300 mb-4 -mt-2" data-testid={`remedy-savings-${p.id}`}>
                            You save {inr(p.regular_price - p.offer_price)}
                          </p>
                        )}
                        {isOwned ? (
                          <Button onClick={() => access(p)} disabled={busy === p.id} className="rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 hover:bg-emerald-500/25" data-testid={`remedy-access-${p.id}`}>
                            {busy === p.id ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} strokeWidth={1.5} />} Access content
                          </Button>
                        ) : (
                          <Button onClick={() => buy(p)} disabled={busy === p.id} className="rounded-full bg-cyan-400/10 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/20" data-testid={`remedy-buy-${p.id}`}>
                            {busy === p.id ? <Loader2 size={15} className="animate-spin" /> : <ShoppingCart size={15} strokeWidth={1.5} />} Buy now
                          </Button>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-500 leading-relaxed border-t border-white/[0.06] mt-14 pt-6">
          Digital products are delivered as secure links after successful payment. These are wellness and self-care resources — not medical, financial or professional advice.
        </p>
      </div>
    </div>
  );
}